import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ExportJsonExcel from "js-export-excel";
import Swal from "sweetalert2";

// Exporta una lista de renglones a Excel preguntando si se quiere la foto del
// modelo. Sin foto usa js-export-excel (inmediato); con foto arma el libro con
// ExcelJS e inserta la imagen de cada articulo en la primera columna.
//
//   exportaExcelConFotoOpcional({
//     filas: [{ _idArticulo, Codigo, Nombre, ... }],   // _idArticulo no se exporta
//     columnas: ["Codigo", "Nombre", ...],
//     nombreArchivo: "Inventario",                      // sin extension
//     hoja: "General",
//   })
//
// Las fotos se bajan de a varias al mismo tiempo y con un aviso de avance: con
// cientos de renglones, bajarlas una por una parecia que el sistema se atoraba.
const EN_PARALELO = 8;   // fotos que se bajan al mismo tiempo
const ESPERA_FOTO = 15000; // ms: si una foto no llega, ese renglon va sin foto

const blobABase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result.split(",")[1] : "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// fetch que no se queda colgado
async function pide(url, opciones) {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const reloj = setTimeout(() => ctrl && ctrl.abort(), ESPERA_FOTO);
  try {
    return await fetch(url, { ...(opciones || {}), signal: ctrl ? ctrl.signal : undefined });
  } finally {
    clearTimeout(reloj);
  }
}

// La liga de la foto guardada en Cloudinary; una sola consulta por id
async function urlMedia(id, urls) {
  if (urls[id] !== undefined) return urls[id];
  let url = "";
  try {
    const r = await pide(`${process.env.REACT_APP_URL_GETMEDIA}/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` },
    });
    if (r.ok) {
      const d = await r.json();
      if (d && d.secure_url) url = d.secure_url;
    }
  } catch (e) { /* sin foto */ }
  urls[id] = url;
  return url;
}

async function bajaImagen(url) {
  try {
    const r = await pide(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    const ext = blob.type.includes("jpeg") ? "jpeg" : blob.type.includes("png") ? "png" : blob.type.includes("gif") ? "gif" : null;
    if (!ext) return null;
    return { base64: await blobABase64(blob), extension: ext };
  } catch (e) {
    return null;
  }
}

// Si el renglon trae color y ese color tiene foto propia se usa esa; si no, la
// foto general del modelo; y si tampoco hay, la imagen de "sin imagen".
async function fotoDe(idArticulo, idColor, cache, urls) {
  const llave = idColor ? `${idArticulo}_${idColor}` : String(idArticulo);
  if (cache[llave] !== undefined) return cache[llave];
  let url = "";
  if (idColor) url = await urlMedia(`${idArticulo}_${idColor}`, urls);
  if (!url) url = await urlMedia(String(idArticulo), urls);
  if (!url) url = process.env.PUBLIC_URL + "/sinImagen.png";
  if (cache[url] === undefined) cache[url] = await bajaImagen(url);
  cache[llave] = cache[url];
  return cache[llave];
}

// Baja las fotos de a EN_PARALELO, avisando el avance
async function bajaFotos(llaves, cache, urls, avanza, cancelado) {
  for (let i = 0; i < llaves.length; i += EN_PARALELO) {
    if (cancelado()) return false;
    const lote = llaves.slice(i, i + EN_PARALELO);
    await Promise.all(lote.map((k) => fotoDe(k.idArticulo, k.idColor, cache, urls)));
    avanza(Math.min(i + EN_PARALELO, llaves.length));
  }
  return true;
}

// Acepta una hoja ({ filas, columnas, hoja }) o varias ({ hojas: [{ nombre, filas, columnas }] }).
// La foto se inserta en toda hoja cuyos renglones traigan _idArticulo.
// siempreConFoto: no pregunta, va directo con foto (p.ej. el Excel de la OC para el proveedor).
export async function exportaExcelConFotoOpcional({ filas, columnas, nombreArchivo, hoja, hojas, siempreConFoto }) {
  const lista = hojas || [{ nombre: hoja || "Hoja1", filas, columnas }];
  const total = lista.reduce((n, h) => n + (h.filas ? h.filas.length : 0), 0);
  if (!total) {
    Swal.fire({ icon: "info", title: "Sin renglones", text: "No hay nada que exportar con los filtros actuales." });
    return;
  }
  const sinId = (f) => { const { _idArticulo, _idColor, ...resto } = f; return resto; };
  const soloDatos = () => {
    new ExportJsonExcel({
      fileName: nombreArchivo,
      datas: lista.map((h) => ({
        sheetData: (h.filas || []).map(sinId), sheetName: h.nombre, sheetFilter: h.columnas, sheetHeader: h.columnas,
      })),
    }).saveExcel();
  };

  // Sin fotos que buscar no tiene caso preguntar
  const hayArticulo = lista.some((h) => (h.filas || []).some((f) => f._idArticulo));
  if (!hayArticulo) { soloDatos(); return; }

  // Aceptar = con foto, Cancelar = solo datos, la X o Escape = no exportar nada
  const res = siempreConFoto ? { isConfirmed: true } : await Swal.fire({
    title: "¿Incluir la foto del modelo?",
    text: `${total} renglones. Con foto tarda más porque descarga cada imagen.`,
    icon: "question",
    showCancelButton: true,
    showCloseButton: true,
    allowOutsideClick: false,
    confirmButtonText: "Sí, con foto",
    cancelButtonText: "No, solo datos",
  });
  if (res.isConfirmed !== true) {
    // Cancelar baja el Excel sin fotos; la X y Escape no bajan nada
    if (res.dismiss === Swal.DismissReason.cancel) soloDatos();
    return;
  }

  // Una sola descarga por articulo (o articulo + color) aunque se repita
  const cache = {}, urls = {};
  const llaves = [];
  const vistas = new Set();
  lista.forEach((h) => (h.filas || []).forEach((f) => {
    if (!f._idArticulo) return;
    const k = f._idColor ? `${f._idArticulo}_${f._idColor}` : String(f._idArticulo);
    if (vistas.has(k)) return;
    vistas.add(k);
    llaves.push({ idArticulo: f._idArticulo, idColor: f._idColor });
  }));

  let cancela = false;
  Swal.fire({
    title: "Descargando fotos",
    html: `0 de ${llaves.length}`,
    allowOutsideClick: false,
    showCancelButton: true,
    cancelButtonText: "Cancelar",
    onBeforeOpen: () => Swal.showLoading(),
  }).then((r) => { if (r.dismiss === Swal.DismissReason.cancel) cancela = true; });

  const termino = await bajaFotos(llaves, cache, urls, (n) => {
    const cont = Swal.getHtmlContainer && Swal.getHtmlContainer();
    if (cont) cont.textContent = `${n} de ${llaves.length}`;
  }, () => cancela);
  if (!termino) { Swal.close(); return; }

  const cont = Swal.getHtmlContainer && Swal.getHtmlContainer();
  if (cont) cont.textContent = "Armando el archivo...";

  const workbook = new ExcelJS.Workbook();
  // cada imagen se guarda una sola vez en el libro aunque se repita en muchos renglones
  const idsImagen = new Map();
  for (const h of lista) {
    const sheet = workbook.addWorksheet(h.nombre);
    const conFoto = (h.filas || []).some((f) => f._idArticulo);
    sheet.addRow(conFoto ? ["Foto", ...h.columnas] : [...h.columnas]);
    sheet.getRow(1).font = { bold: true };
    const anchos = h.columnas.map((c) => ({ width: c.length > 10 ? 22 : 14 }));
    sheet.columns = conFoto ? [{ width: 12 }, ...anchos] : anchos;
    for (let i = 0; i < (h.filas || []).length; i++) {
      const f = h.filas[i];
      const datos = h.columnas.map((c) => f[c]);
      sheet.addRow(conFoto ? ["", ...datos] : datos);
      if (!conFoto) continue;
      const rowIdx = i + 2;
      sheet.getRow(rowIdx).height = 48;
      const llave = f._idArticulo ? (f._idColor ? `${f._idArticulo}_${f._idColor}` : String(f._idArticulo)) : "";
      const img = llave ? cache[llave] : null;
      if (img) {
        if (!idsImagen.has(img)) idsImagen.set(img, workbook.addImage(img));
        const id = idsImagen.get(img);
        sheet.addImage(id, { tl: { col: 0, row: rowIdx - 1 }, ext: { width: 60, height: 60 } });
      }
    }
  }
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${nombreArchivo}.xlsx`);
  Swal.close();
}

export default exportaExcelConFotoOpcional;
